/**
 * Staffing statistics for the coordination workbench (part 3e, 18 Sep 2026). Pure functions.
 *
 * Her Overview tab and dashboard tiles, computed from bookings: fully staffed share, slots filled per
 * role, the 30/60/90/120-day windows, remaining weddings this year and next, and this week's activity.
 * "Fully staffed" uses the shared staffing rule (a real person in every slot the package calls for).
 * Horizon: the next N days (her dashboard's 120) or every upcoming event (no cap).
 */
import { getStaffingNeeds, ROLES, roleOccupant } from '../booking/staffing.js'
import { roleApplies } from './actionCenter.js'
import { daysOut, addDays } from './queues.js'

export const HORIZONS = [
  { key: '120', label: 'Next 120 days', days: 120 },
  { key: 'all', label: 'All upcoming', days: null }
]
export const WINDOWS = [
  { key: '0_30', label: '0-30 days', from: 0, to: 30 },
  { key: '31_60', label: '31-60 days', from: 31, to: 60 },
  { key: '61_90', label: '61-90 days', from: 61, to: 90 },
  { key: '91_120', label: '91-120 days', from: 91, to: 120 }
]

const pct = (num, den) => (den > 0 ? Math.round((num / den) * 100) : null)

export function isFullyStaffedForCoordinator(booking) {
  const n = getStaffingNeeds(booking)
  return !n.photographer && !n.videographer && !n.secondPhotographer && !n.secondVideographer
}

/** Slots the package calls for and how many hold a real person, per role. */
export function slotTotals(bookings) {
  const out = {}
  for (const role of ROLES) out[role] = { needed: 0, filled: 0 }
  for (const b of bookings || []) {
    for (const role of ROLES) {
      if (!roleApplies(b, role)) continue
      out[role].needed += 1
      if (roleOccupant(b, role).kind === 'talent') out[role].filled += 1
    }
  }
  return out
}

function summarize(bookings) {
  const total = (bookings || []).length
  const staffed = (bookings || []).filter(isFullyStaffedForCoordinator).length
  return { events: total, staffed, pct: pct(staffed, total) }
}

/**
 * Everything the stats card shows.
 * `rows`: upcoming bookings (not cancelled, not test) with add_ons shaped for the staffing rule.
 * `outreachByKey`: Map `${booking_id}:${role}` → coordinator_outreach row.
 * `activity`: { assignments7d: [{role}], newBookings7d, leads7d, awaitingReply, listedOnIndeed } from the caller.
 */
export function staffingStats(rows, { today, horizonKey = '120', outreachByKey = new Map() } = {}) {
  const horizon = HORIZONS.find((h) => h.key === horizonKey) || HORIZONS[0]
  const upcoming = (rows || []).filter((b) => daysOut(b.event_date, today) >= 0)
  const inHorizon = horizon.days === null ? upcoming : upcoming.filter((b) => daysOut(b.event_date, today) <= horizon.days)

  const slots = slotTotals(inHorizon)
  const openSlots = ROLES.reduce((s, r) => s + (slots[r].needed - slots[r].filled), 0)

  // Open slots nobody has started on, per role, in the horizon (her "outreach hasn't started yet").
  const notStarted = {}
  for (const role of ROLES) notStarted[role] = 0
  for (const b of inHorizon) {
    for (const role of ROLES) {
      if (!roleApplies(b, role) || roleOccupant(b, role).kind === 'talent') continue
      const o = outreachByKey.get(`${b.id}:${role}`)
      if (!o || o.status === 'not_started') notStarted[role] += 1
    }
  }

  const windows = WINDOWS.map((w) => {
    const inWin = upcoming.filter((b) => { const d = daysOut(b.event_date, today); return d >= w.from && d <= w.to })
    return { ...w, ...summarize(inWin) }
  })
  const beyond = upcoming.filter((b) => daysOut(b.event_date, today) > 120)

  const thisYear = String(today).slice(0, 4)
  const nextYear = String(Number(thisYear) + 1)
  const years = [thisYear, nextYear].map((y) => ({ year: y, ...summarize(upcoming.filter((b) => String(b.event_date).slice(0, 4) === y)) }))

  return {
    horizon: horizon.key,
    horizon_label: horizon.label,
    horizon_end: horizon.days === null ? null : addDays(today, horizon.days),
    summary: summarize(inHorizon),
    slots,
    open_slots: openSlots,
    not_started: notStarted,
    windows,
    beyond_120: summarize(beyond),
    years
  }
}

/** This week's movement from the log tables; pure so it can be tested with plain arrays. */
export function weeklyActivity({ assignments = [], newBookings = 0, leads = [], today, days = 7 } = {}) {
  const since = addDays(today, -days)
  const recentAssign = assignments.filter((a) => String(a.created_at || '').slice(0, 10) >= since)
  const byRole = { photographer: 0, videographer: 0, second_photographer: 0 }
  for (const a of recentAssign) if (byRole[a.role] !== undefined) byRole[a.role] += 1
  const recentLeads = leads.filter((l) => String(l.contacted_at || '').slice(0, 10) >= since)
  return {
    days,
    since,
    assignments: recentAssign.length,
    assignments_by_role: byRole,
    new_bookings: newBookings,
    outreach: recentLeads.length,
    listed_on_indeed: leads.filter((l) => l.source === 'indeed' && !l.outcome).length,
    awaiting_reply: leads.filter((l) => !l.outcome).length
  }
}
